/**
 * Action dialogs opened from the creative detail modal:
 *  - RegenerateDialog → creatives.regenerate_asset {brandId, creativeId, aspect, instruction (≥3 chars),
 *                       mode: "edit" (only the flawed element; images) | "regenerate" (fresh take)} → {jobId}
 *  - IterateDialog    → creatives.iterate {brandId, creativeId, mode: "variations"|"bench_rebuild"|"replace",
 *                       count: 1–8, keep?: ["angle"] (replace), reason?} → {batchId, creativeIds, jobId, results, batchIds}
 *  - PublishDialog    → connections.list {brandId} → {providers:[{provider, kind, connected, selectedAccountId}]}
 *                       → ads.publish {brandId, creativeId, platform, destinationKey?}  (PAUSED; lands in the
 *                       broad ad set from brand config — the tool takes no ad-set override)
 *  - ActivateDialog   → explicit spend warning first, then ads.activate {brandId, adId} (human-only;
 *                       the server's two-step confirmation follows via runTool)
 */
import { AlertTriangle, ArrowRight, CirclePause, Layers, Lock, Minus, Plus, Rocket, Wand2, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useTool, useToolMutation } from "@/api/hooks";
import { toast } from "@/api/toast-store";
import { pickArray, type AdRow, type Creative, type CreativeAsset, type QaIssue } from "@/api/types";
import { cn } from "@/lib/cn";
import { humanize } from "@/lib/format";
import { brandPath, useCurrentBrand } from "@/state/brand";
import { Pill } from "../ui/Badge";
import { Button } from "../ui/Button";
import { EmptyState, ErrorState, Skeleton } from "../ui/Feedback";
import { Checkbox, Chip, Field, Input, Segmented, SelectPill, Textarea, type SelectOption } from "../ui/Form";
import { LogoTile, platformLabel } from "../ui/Logos";
import { Modal } from "../ui/Overlay";
import { CreativeMedia } from "./CreativeBits";
import { useTopmostClose } from "./hooks";
import { pickBatchId } from "./normalize";
import { generationHref, generationReceipt } from "./GenerationProgress";

// ─── Regenerate element ───────────────────────────────────────────────────────

const STANDARD_ELEMENTS = ["headline", "text line 2", "CTA button", "background", "product", "logo", "people"];

export function RegenerateDialog({
  open,
  onClose,
  brandId,
  creative,
  initialAssetId,
  initialIssue,
}: {
  open: boolean;
  onClose: () => void;
  brandId: string;
  creative: Creative;
  initialAssetId?: string | null;
  initialIssue?: QaIssue | null;
}) {
  const { marker, close } = useTopmostClose(onClose);
  const assets = useMemo(() => (creative.assets ?? []).filter((a) => a.status !== "superseded"), [creative.assets]);
  const isVideo = creative.kind === "video";
  const [assetId, setAssetId] = useState<string | null>(null);
  const [element, setElement] = useState<string>("");
  const [custom, setCustom] = useState("");
  const [instruction, setInstruction] = useState("");
  const [mode, setMode] = useState<"edit" | "regenerate">("edit");
  useEffect(() => {
    if (!open) return;
    setAssetId(initialAssetId ?? assets.find((a) => (a.qaStatus ?? a.qaReport?.verdict) === "fail")?.id ?? assets[0]?.id ?? null);
    setElement(initialIssue?.element ?? "");
    setCustom("");
    setInstruction(initialIssue?.detail ? `Fix: ${initialIssue.detail}` : "");
    setMode("edit");
  }, [open, initialAssetId, initialIssue, assets]);
  const asset = assets.find((a) => a.id === assetId) ?? null;
  const qaElements = (asset?.qaReport?.issues ?? []).map((i) => i.element).filter((x): x is string => !!x);
  const elements = Array.from(new Set([...qaElements, ...STANDARD_ELEMENTS]));
  const finalElement = element === "__other" ? custom.trim() : element;
  const text = [finalElement && mode === "edit" ? `Only change the ${finalElement}.` : finalElement ? `Focus on the ${finalElement}.` : "", instruction.trim()].filter(Boolean).join(" ");
  const regen = useToolMutation("creatives.regenerate_asset", {
    onSuccess: (res) => {
      const receipt = generationReceipt(res);
      toast({ tone: "success", title: mode === "edit" && !isVideo ? "Element fix requested" : "New take requested", body: "The new version goes through QA and your approval queue.", action: receipt ? { label: "View flow", href: generationHref(brandId, receipt) } : { label: "View creations", href: brandPath(brandId, `creations?creative=${encodeURIComponent(creative.id)}`) } });
      onClose();
    },
  });
  const can = !!asset && instruction.trim().length >= 3;
  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      className={marker}
      title={
        <span className="flex items-center gap-2">
          <Wand2 className="size-4.5 text-lavender" strokeWidth={1.75} />
          Regenerate an element
        </span>
      }
      description="Fix only what's wrong — the old version is kept as history and the new one goes through vision QA and back to your approval queue."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!can}
            loading={regen.isPending}
            icon={<Wand2 className="size-3.5" />}
            onClick={() => asset && regen.mutate({ brandId, creativeId: creative.id, aspect: asset.aspect, instruction: text.slice(0, 2000), mode: isVideo ? "regenerate" : mode })}
          >
            {mode === "edit" && !isVideo ? "Fix element" : "Regenerate"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Aspect">
          {assets.length === 0 ? (
            <p className="text-[12.5px] text-muted">This creative has no rendered assets yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="Asset">
              {assets.map((a) => (
                <AssetChoice key={a.id} asset={a} selected={a.id === assetId} onSelect={() => setAssetId(a.id)} />
              ))}
            </div>
          )}
        </Field>
        {!isVideo && (
          <Field label="Mode">
            <Segmented
              size="sm"
              value={mode}
              onChange={setMode}
              options={[
                { value: "edit", label: "Edit element" },
                { value: "regenerate", label: "Fresh take" },
              ]}
            />
            <span className="text-[11.5px] text-muted">
              {mode === "edit" ? "Targeted edit: only the described element changes." : "New render of this aspect with your instruction as a correction."}
            </span>
          </Field>
        )}
        <Field label="Flawed element" hint={qaElements.length ? "Elements flagged by QA are marked." : undefined}>
          <div className="flex flex-wrap gap-1.5">
            {elements.map((e) => (
              <Chip key={e} selected={element === e} onClick={() => setElement(element === e ? "" : e)}>
                {qaElements.includes(e) && <span className="size-1.5 rounded-full bg-danger" aria-hidden />}
                {e}
              </Chip>
            ))}
            <Chip selected={element === "__other"} onClick={() => setElement(element === "__other" ? "" : "__other")}>
              Other…
            </Chip>
          </div>
          {element === "__other" && <Input className="mt-2" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. price badge" autoFocus />}
        </Field>
        <Field label="Instruction" required hint="Be specific — exact text renders literally.">
          <Textarea rows={3} value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="e.g. Re-render the headline in a clean bold sans-serif, same words." />
        </Field>
      </div>
    </Modal>
  );
}

function AssetChoice({ asset, selected, onSelect }: { asset: CreativeAsset; selected: boolean; onSelect: () => void }) {
  const tall = asset.aspect === "9:16" || asset.aspect === "4:5";
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn("flex flex-col items-center gap-1.5 rounded-xl border p-1.5 transition-colors", selected ? "border-primary bg-primary-soft" : "border-line hover:border-line-strong")}
    >
      <span className={cn("overflow-hidden rounded-lg", tall ? "h-24 w-[54px]" : "size-24")}>
        <CreativeMedia url={asset.url} poster={asset.thumbnailUrl} mediaType={asset.mediaType} className="size-full" />
      </span>
      <span className={cn("text-[11px] font-medium", selected ? "text-lavender" : "text-muted")}>
        {asset.aspect}
        {(asset.qaStatus ?? asset.qaReport?.verdict) === "fail" && <span className="ml-1 text-danger">· QA</span>}
      </span>
    </button>
  );
}

// ─── Iterate ──────────────────────────────────────────────────────────────────

export function Stepper({ value, onChange, min = 1, max = 8, label }: { value: number; onChange: (v: number) => void; min?: number; max?: number; label: string }) {
  return (
    <div className="inline-flex h-8 items-center rounded-lg border border-line-strong bg-surface-2" role="group" aria-label={label}>
      <button
        type="button"
        className="grid h-full w-8 place-items-center text-muted hover:text-fg disabled:opacity-35"
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        aria-label={`Decrease ${label}`}
      >
        <Minus className="size-3.5" />
      </button>
      <span className="min-w-10 text-center text-[13px] font-medium tnum" aria-live="polite">
        {value}/{max}
      </span>
      <button
        type="button"
        className="grid h-full w-8 place-items-center text-muted hover:text-fg disabled:opacity-35"
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
        aria-label={`Increase ${label}`}
      >
        <Plus className="size-3.5" />
      </button>
    </div>
  );
}

export type IterateMode = "variations" | "bench_rebuild" | "replace";

const ITERATE_MODES: { value: IterateMode; label: string; body: string }[] = [
  { value: "variations", label: "Variations", body: "New visuals, SAME hook, angle, trigger and stage — the proven variables." },
  { value: "bench_rebuild", label: "Bench rebuild", body: "A winner is fatiguing: fresh executions of the same hook and angle." },
  { value: "replace", label: "Replace", body: "It's wasting spend: keep the angle, new hooks (trigger, stage and format stay)." },
];

export function IterateDialog({
  open,
  onClose,
  brandId,
  creative,
  initialMode = "variations",
}: {
  open: boolean;
  onClose: () => void;
  brandId: string;
  creative: Creative;
  initialMode?: IterateMode;
}) {
  const { marker, close } = useTopmostClose(onClose);
  const [n, setN] = useState(3);
  const [mode, setMode] = useState<IterateMode>(initialMode);
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) return;
    setN(3);
    setMode(initialMode);
    setReason("");
  }, [open, initialMode]);
  const iterate = useToolMutation<unknown>("creatives.iterate", {
    onSuccess: (res) => {
      const batchId = pickBatchId(res);
      const receipt = generationReceipt(res);
      toast({
        tone: "success",
        title: mode === "replace" ? `Replacing — ${n} new execution${n > 1 ? "s" : ""} with fresh hooks` : `Iterating — ${n} new execution${n > 1 ? "s" : ""} of the same hook & angle`,
        body: "Each goes through generation → QA → your approval queue.",
        action: receipt ? { label: "View flow", href: generationHref(brandId, receipt) } : batchId ? { label: "Track in Studio", href: brandPath(brandId, `studio?batch=${encodeURIComponent(batchId)}`) } : { label: "View creations", href: brandPath(brandId, "creations") },
      });
      onClose();
    },
  });
  const kept = mode === "replace" ? "Angle, trigger, stage and format kept" : "Kept identical";
  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      className={marker}
      title={
        <span className="flex items-center gap-2">
          <Layers className="size-4.5 text-lavender" strokeWidth={1.75} />
          Iterate this creative
        </span>
      }
      description="New executions that point back at this creative (generation + 1). Not blocked by the 72h no-touch window."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={iterate.isPending}
            icon={<Layers className="size-3.5" />}
            onClick={() =>
              iterate.mutate({
                brandId,
                creativeId: creative.id,
                mode,
                count: n,
                ...(mode === "replace" ? { keep: ["angle"] } : {}),
                ...(reason.trim() ? { reason: reason.trim().slice(0, 2000) } : {}),
              })
            }
          >
            Generate {n} variant{n > 1 ? "s" : ""}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Iteration mode">
          {ITERATE_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={mode === m.value}
              onClick={() => setMode(m.value)}
              className={cn("rounded-xl border p-2.5 text-left transition-colors", mode === m.value ? "border-primary bg-primary-soft" : "border-line hover:border-line-strong hover:bg-hover")}
            >
              <span className={cn("block text-[13px] font-medium", mode === m.value && "text-lavender")}>{m.label}</span>
              <span className="mt-0.5 block text-[11.5px] leading-snug text-muted">{m.body}</span>
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-line bg-surface-2 p-3.5">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-subtle">
            <Lock className="size-3" /> {kept}
          </div>
          <p className={cn("text-[14px] font-medium leading-snug", mode === "replace" && "text-muted line-through decoration-subtle")}>“{creative.hook ?? creative.name}”</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {creative.angle && <Pill size="xs">Angle · {creative.angle}</Pill>}
            {creative.emotionalTrigger && (
              <Pill size="xs" tone="primary">
                {humanize(String(creative.emotionalTrigger))}
              </Pill>
            )}
            {creative.awarenessStage && (
              <Pill size="xs" tone="info">
                {humanize(String(creative.awarenessStage))}
              </Pill>
            )}
            {typeof creative.generation === "number" && (
              <Pill size="xs" tone="neutral">
                Gen {creative.generation} → {creative.generation + 1}
              </Pill>
            )}
          </div>
        </div>
        <Field label="New executions">
          <Stepper value={n} onChange={setN} min={1} max={8} label="variants" />
        </Field>
        <Field label="Direction (optional)" hint="Passed to the planner, e.g. what to change between executions.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Try a before/after layout and a testimonial card; keep the bold sans headline." />
        </Field>
      </div>
    </Modal>
  );
}

// ─── Publish ──────────────────────────────────────────────────────────────────

interface ProviderRow {
  provider: string;
  name?: string;
  kind?: string;
  connected?: boolean;
  selectedAccountId?: string | null;
}

const AD_PLATFORMS = new Set(["meta", "tiktok", "apple_search_ads", "google_ads"]);

export function PublishDialog({ open, onClose, brandId, creative }: { open: boolean; onClose: () => void; brandId: string; creative: Creative }) {
  const { marker, close } = useTopmostClose(onClose);
  const { brand } = useCurrentBrand();
  const conns = useTool<unknown>("connections.list", open ? { brandId } : null);
  const providers = useMemo(
    () => pickArray<ProviderRow>(conns.data, "providers", "connections").filter((p) => p && AD_PLATFORMS.has(p.provider) && (p.kind ? p.kind === "ad_platform" : true) && p.connected !== false),
    [conns.data],
  );
  const [platform, setPlatform] = useState<string | null>(null);
  const [destinationKey, setDestinationKey] = useState<string>("__default");
  useEffect(() => {
    if (open && !platform && providers[0]) setPlatform(providers[0].provider);
  }, [open, platform, providers]);
  useEffect(() => {
    if (open) setDestinationKey("__default");
  }, [open, platform]);
  const destinations = (brand?.config?.destinations ?? []).filter((d) => !platform || d.platform === "any" || d.platform === platform);
  const destOptions: SelectOption[] = [
    {
      value: "__default",
      label: creative.destinationKey ? `Creative's destination (${creative.destinationKey})` : "Platform default",
      description: "Resolved from brand config — the URL is used verbatim, macros intact.",
    },
    ...destinations.map((d) => ({ value: d.key, label: d.key, description: d.description ?? d.url })),
  ];
  const broadAdSet = platform ? (brand?.config?.structure as Record<string, { broadAdSetId?: string } | undefined> | undefined)?.[platform]?.broadAdSetId : undefined;
  const publish = useToolMutation("ads.publish", {
    label: "Publish creative (PAUSED)",
    success: "Published PAUSED — activate it when you're ready",
    onSuccess: onClose,
  });
  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      className={marker}
      title={
        <span className="flex items-center gap-2">
          <Rocket className="size-4.5 text-lavender" strokeWidth={1.75} />
          Publish to an ad account
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!platform}
            loading={publish.isPending}
            icon={<CirclePause className="size-3.5" />}
            onClick={() => platform && publish.mutate({ brandId, creativeId: creative.id, platform, ...(destinationKey !== "__default" ? { destinationKey } : {}) })}
          >
            Publish paused
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-3 rounded-xl border border-info/30 bg-info-soft/40 p-3 text-[12.5px] leading-relaxed text-fg/90">
          <CirclePause className="mt-0.5 size-4 shrink-0 text-info" strokeWidth={1.75} />
          <div>
            Everything publishes <b>PAUSED</b> — nothing spends until a human activates it. Both aspect ratios are uploaded, the destination URL comes verbatim from brand config (macros intact),
            multi-advertiser ads are opted out, and the write is verified by readback. A 72h no-touch window starts at launch.
          </div>
        </div>
        <Field label="Platform">
          {conns.isLoading ? (
            <div className="flex gap-2">
              <Skeleton className="h-14 flex-1" />
              <Skeleton className="h-14 flex-1" />
            </div>
          ) : conns.error && !conns.data ? (
            <ErrorState error={conns.error} onRetry={() => void conns.refetch()} compact />
          ) : providers.length === 0 ? (
            <EmptyState
              compact
              title="No ad account connected"
              body="Connect Meta, TikTok, Google Ads or Apple Search Ads to publish."
              action={
                <Link to={brandPath(brandId, "connections")} className="inline-flex items-center gap-1 text-[13px] text-link hover:text-lavender">
                  Open Connections <ArrowRight className="size-3.5" />
                </Link>
              }
            />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Platform">
              {providers.map((p) => (
                <button
                  key={p.provider}
                  type="button"
                  role="radio"
                  aria-checked={platform === p.provider}
                  onClick={() => setPlatform(p.provider)}
                  className={cn(
                    "flex items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                    platform === p.provider ? "border-primary bg-primary-soft" : "border-line hover:border-line-strong hover:bg-hover",
                  )}
                >
                  <LogoTile platform={p.provider} />
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-medium">{p.name ?? platformLabel(p.provider)}</span>
                    <span className="block truncate text-[11.5px] text-muted">{p.selectedAccountId ? `Account ${p.selectedAccountId}` : "No ad account selected"}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Field>
        {platform && (
          <>
            <Field label="Ad set">
              <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2 text-[12.5px]">
                <Lock className="size-3.5 shrink-0 text-muted" />
                <span className="min-w-0">
                  Broad ad set of the scaling CBO
                  {broadAdSet ? <code className="ml-1 rounded bg-elevated px-1 font-mono text-[11px]">{broadAdSet}</code> : <span className="text-amber"> — not set in brand config yet</span>}
                  <span className="block text-[11.5px] text-muted">One scaling CBO, one broad ad set, all creatives inside.</span>
                </span>
              </div>
            </Field>
            <Field label="Destination">
              <SelectPill variant="field" value={destinationKey} options={destOptions} onChange={setDestinationKey} />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Activate ─────────────────────────────────────────────────────────────────

export function ActivateDialog({ open, onClose, brandId, ad }: { open: boolean; onClose: () => void; brandId: string; ad: AdRow | null }) {
  const { marker, close } = useTopmostClose(onClose);
  const [ack, setAck] = useState(false);
  useEffect(() => {
    if (open) setAck(false);
  }, [open]);
  const activate = useToolMutation("ads.activate", { success: "Ad activated — it's now spending", onSuccess: onClose });
  if (!ad) return null;
  return (
    <Modal
      open={open}
      onClose={close}
      size="sm"
      className={marker}
      title={
        <span className="flex items-center gap-2.5">
          <span className="grid size-8 place-items-center rounded-full bg-warning-soft text-warning">
            <Zap className="size-4" />
          </span>
          Activate this ad?
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Keep paused
          </Button>
          <Button variant="primary" disabled={!ack} loading={activate.isPending} onClick={() => activate.mutate({ brandId, adId: ad.id })}>
            Continue
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13.5px] leading-relaxed">
        <div className="flex items-center gap-2.5 rounded-xl border border-line bg-surface-2 p-3">
          <LogoTile platform={String(ad.platform ?? "")} size="sm" />
          <div className="min-w-0">
            <div className="truncate font-medium">{ad.name}</div>
            <div className="text-[11.5px] text-muted">
              {platformLabel(ad.platform)} · {String(ad.status ?? "paused").toLowerCase()}
            </div>
          </div>
        </div>
        <p className="flex gap-2 text-fg/90">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
          <span>
            Activating turns this ad <b>ACTIVE</b> and it <b>starts spending</b> from its ad set / campaign budget right away. It's refused if that budget was increased today (never scale and swap
            creative the same day).
          </span>
        </p>
        <p className="text-[12.5px] text-muted">The server will ask you to confirm the exact budget it draws from next.</p>
        <Checkbox checked={ack} onChange={setAck} label="I understand this ad will start spending" />
      </div>
    </Modal>
  );
}
